/**
 * @file routes/_app/dispatch/new.tsx
 * @description 新建驗證案件 — 單一表單頁面。
 *
 * 設計：
 * - 不再使用多步驟精靈，所有欄位平鋪於一張表單，一次提交即建立案件。
 * - 欄位參考紙本「有機農產品驗證申請表」：客戶 / 驗證方案 / 驗證選項（個別·集團）/
 *   輔導員 / 驗證類型（驗證服務類型）/ 驗證面積 / 產品銷售對象·通路 / 備注。
 * - 驗證服務類型（verificationType）可複選：前端多選後以分隔符（@zhcb/shared
 *   VERIFICATION_TYPE_SEPARATOR）拼成單一字串落庫，相容既有 varchar 單欄存儲。
 * - 系統既有欄位（clientId / scheme / verificationType）落於 cases 主表；
 *   紙本其餘結構化欄位落於 cases.application_details（jsonb，schema 見 @zhcb/shared）。
 * - 申請明細按驗證方案分流（2026-07-04 客戶 PPT S9/S10，schema 見
 *   @zhcb/shared schemas/case-application）：
 *   - 有機 TOP（ECO 暫按 TOP）：驗證品項（作物字典）/ 驗證範圍（自由文字，序6）
 *     （Form.List 多組，每組可附件上傳識別）+ 驗證面積（4 位小數公頃字串）；
 *     提交前比對作物管理，尚未建立的驗證範圍逐項提示是否新增（序7）；
 *   - 產銷履歷 TAP：5 欄多行明細 {產品類別/產品品項/作物產期/申請面積/合理採收量}
 *     （每行可附件）+ 生產地筆數 + 場區筆數 + 驗證總面積（各行申請面積前端即時加總、
 *     後端寫入前重算，sumTapArea 整數萬分位）；（序 56：原「生產地及場區筆數」拆兩欄）
 *     整區非必填（序16）；
 *   - 申請明細欄位區塊與修改案件抽屜共用 ApplicationDetailsFields（序14）。
 *   - 兩者共同：驗證場所地址 / 採收後處理場所地址——選定客戶後自
 *     GET /api/clients/:id/lands、/processing-sites 拉取客戶檔案土地 / 採後場所
 *     勾選帶入（id + 地址文字快照存入 applicationDetails；客戶檔案仍是 master）。
 *   - 「附件上傳識別」現階段 = 直傳 OSS 存 fileIds（AI 識別自動填行留待後續）。
 * - 申請文件上傳已移除：建案後到案件詳情頁的「文件」分頁補傳。
 * - 到期日（SLA 基準）仍於「派遣」環節設定，建案時不填。
 *
 * 提交成功後跳轉案件詳情 /dispatch/:caseId。
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { message } from '@/lib/antd-app';
import {
  Button,
  Checkbox,
  Form,
  Input,
  InputNumber,
  Modal,
  Radio,
  Select,
  Space,
  Spin,
  Typography,
  Upload,
} from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, ClipboardList, Plus } from 'lucide-react';
import { apiClient } from '@/lib/api-client';
import {
  VERIFICATION_TYPES,
  VERIFICATION_SCHEMES,
  SCHEME_LABELS,
  applicationKindForScheme,
  isTrackingVerificationType,
  joinVerificationTypes,
  sumTapArea,
  type ApplicationDetails,
  type CaseApplicationDetails,
  type SalesChannel,
  type VerificationScheme,
} from '@zhcb/shared';
import {
  useContractSources,
  useVerificationCategories,
  useVerificationTypes,
} from '@/hooks/useClientLookups';
import { useCropOptions } from '@/hooks/useCrops';
import { PageShell, SectionCard, FormSection } from '@/components/shell';
import { findTopItemKeyByName } from '@/components/dispatch/top-crop-options';
import { confirmYearlyDuplicateCases } from '@/components/dispatch/confirmYearlyDuplicateCases';
import {
  ApplicationDetailsFields,
  confirmAndEnsureTopScopes,
} from '@/components/dispatch/ApplicationDetailsFields';
import {
  buildTapRows,
  buildTopAreaFields,
  buildTopProductEntries,
  splitScopeText,
  type ApplicationDetailsFormValues,
  type TapRowValue,
  type TopProductGroupValue,
} from '@/components/dispatch/application-details-form';
import { uploadFile, type UploadedFile } from '@/lib/file-upload';
import { FileLink } from '@/components/common/FileLink';

/** TanStack Router 路由定義 */
export const Route = createFileRoute('/_app/dispatch/new')({ component: NewCasePage });

// ─── 類型與常量 ───────────────────────────────────────────────────────────────

interface Client {
  id: number;
  clientCode: string;
  name: string;
  /** 客戶綁定的驗證方案；選定客戶後案件方案自動帶入並鎖定，確保與客戶碼一致 */
  scheme: VerificationScheme;
}

interface CreatedCase {
  id: number;
  caseCode: string;
}

/** 產品銷售對象 / 通路選項（對應紙本勾選項）。 */
const SALES_CHANNEL_OPTIONS: { value: SalesChannel; label: string }[] = [
  { value: 'auction', label: '拍賣市場' },
  { value: 'supermarket', label: '超市賣場' },
  { value: 'processor', label: '加工業者' },
  { value: 'distributor', label: '分裝 / 流通業者' },
  { value: 'other', label: '其他銷售對象' },
];

/** 銷售對象 / 通路「名稱」欄的操作提示（2026-09-12 回報序13：支援輸入多項） */
const SALES_NAME_MULTI_HINT = '支援輸入多項，多個名稱請以「、」分隔';

/** 客戶土地（GET /api/clients/:id/lands 回傳項，只取本頁需要的欄位） */
interface ClientLandItem {
  id: number;
  parcelNo: string | null;
  address: string;
}

/** 客戶採收後處理場所（GET /api/clients/:id/processing-sites 回傳項） */
interface ClientSiteItem {
  id: number;
  name: string | null;
  address: string;
}

/** 土地快照文字：地址（＋地號，便於追溯） */
function landSnapshotText(l: ClientLandItem): string {
  return l.parcelNo?.trim() ? `${l.address}（地號 ${l.parcelNo.trim()}）` : l.address;
}

/** 採後場所快照文字：場所名稱：地址 */
function siteSnapshotText(s: ClientSiteItem): string {
  return s.name?.trim() ? `${s.name.trim()}：${s.address}` : s.address;
}

/**
 * 表單值結構（平鋪，提交時再組裝成 applicationDetails）。
 * 申請明細欄位（topProductGroups / 面積 / tapRows / 筆數）見 ApplicationDetailsFormValues。
 */
interface FormValues extends ApplicationDetailsFormValues {
  clientId: number;
  scheme: VerificationScheme;
  /** 合約來源（自客戶下放至案件） */
  contractSource: string;
  /** 驗證服務類型（可複選；提交時以分隔符拼成單一字串落庫） */
  verificationType: string[];
  /** 驗證類別（依方案過濾，可複選） */
  verificationCategories?: string[];
  verificationOption?: 'individual' | 'group';
  groupFarmerCount?: number;
  // —— 場所選定（TOP / TAP 共同；自客戶檔案勾選帶入快照）——
  /** 勾選的客戶土地 id（驗證場所地址） */
  selectedLandIds?: number[];
  /** 勾選的採後處理場所 id（採收後處理場所地址） */
  selectedSiteIds?: number[];
  salesChannels?: SalesChannel[];
  salesAuctionName?: string;
  salesSupermarketName?: string;
  salesProcessorName?: string;
  salesDistributorName?: string;
  salesOtherName?: string;
  notes?: string;
}

// ─── 頁面組件 ─────────────────────────────────────────────────────────────────

/**
 * 新建驗證案件單表單頁。
 */
function NewCasePage() {
  const navigate = useNavigate();
  const [form] = Form.useForm<FormValues>();

  // 序109：申請表上傳（建案後自動掛載為應附文件「申請書」）＋讀取填充
  const [applicationFile, setApplicationFile] = useState<UploadedFile | null>(null);
  const [applicationUploading, setApplicationUploading] = useState(false);

  /** 序109：讀取申請表內容並填充表單（提取值可修改；失敗提示人工填寫） */
  const extractMutation = useMutation({
    mutationFn: (fileId: number) =>
      apiClient.post<{
        item: {
          counselorName?: string;
          counselorPhone?: string;
          productItems?: string[];
          productScopes?: string[];
          verificationArea?: string;
          landCount?: number;
          siteCount?: number;
        };
      }>('/api/ai/extract-application', { fileId }),
    onSuccess: (res) => {
      const it = res.item;
      // 序164：品項/範圍改聯動分組後，AI 讀取結果無法自動配對「哪個範圍屬於哪個品項」，
      // 折衷做法：品項各自成一組（範圍留空待人工從作物字典勾選），原始範圍文字併入備注，
      // 避免辨識結果被靜默丟棄，同時提示使用者手動核對配對。
      // 只有能對上作物字典品項層的名稱才回填下拉，對不上的留空讓使用者自行重選，
      // 避免把無效值塞進 Select（辨識原文仍在下方備注，不會被靜默丟棄）。
      const extractedGroups: TopProductGroupValue[] = (it.productItems ?? []).map((name) => ({
        itemKey: findTopItemKeyByName(cropItems, name),
      }));
      // 序6 範圍改自由文字後：僅一個品項時可直接把辨識出的範圍填入該組；多品項仍需人工配對
      const extractedScopes = splitScopeText((it.productScopes ?? []).join('、'));
      if (extractedGroups.length === 1 && extractedScopes.length) {
        extractedGroups[0]!.scopeText = extractedScopes.join('、');
      }
      form.setFieldsValue({
        // 序137：輔導員欄位已移除，AI 提取結果不再回填輔導員
        ...(extractedGroups.length ? { topProductGroups: extractedGroups } : {}),
        ...(it.verificationArea
          ? { topApplyAreaHa: Number(it.verificationArea) || undefined }
          : {}),
        ...(typeof it.landCount === 'number' ? { tapLandCount: it.landCount } : {}),
        ...(typeof it.siteCount === 'number' ? { tapSiteCount: it.siteCount } : {}),
      });
      message.success(
        it.productScopes?.length && extractedGroups.length !== 1
          ? `已讀取申請表並填充可識別欄位，請核對修改；辨識出的驗證範圍「${it.productScopes.join('、')}」請手動填入對應品項`
          : '已讀取申請表並填充可識別欄位，請核對修改',
      );
    },
    onError: (err: Error) => {
      console.error('[dispatch/new] extract application failed', err);
      message.warning(err.message || '申請表讀取失敗，請人工填寫');
    },
  });

  // 客戶清單：伺服器端搜尋（非一次性拉取後前端過濾）。
  // ⚠️ 舊版一次性拉 `pageSize=200`，但後端分頁上限為 100，客戶數一旦超過 100（依 id desc
  // 排序，取最新 100 筆）較舊的客戶就永遠不會出現在下拉選項——即便輸入其驗證註冊碼 /
  // 名稱也搜不到、無法派案。改為輸入時才帶 `search=` 打 `/api/clients`（client.read，
  // 全角色可讀，亦與後端路由文檔「派案選擇客戶」的設計用途一致），無論客戶總數多少都能找到。
  const [clientSearchInput, setClientSearchInput] = useState('');
  const [debouncedClientSearch, setDebouncedClientSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedClientSearch(clientSearchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [clientSearchInput]);
  const { data: clientsData, isFetching: clientsLoading } = useQuery<{ items: Client[] }>({
    queryKey: ['clients-for-new-case', debouncedClientSearch],
    queryFn: () => {
      const params = new URLSearchParams({ pageSize: '50' });
      if (debouncedClientSearch) params.set('search', debouncedClientSearch);
      return apiClient.get(`/api/clients?${params.toString()}`);
    },
  });
  const clients = clientsData?.items ?? [];
  // 已選客戶：獨立於目前搜尋結果保存，避免使用者選定後又輸入新關鍵字，導致舊結果集
  // 被替換而 `clients.find` 找不到已選客戶（連帶讓下拉框顯示原始數字 id 而非客戶名）。
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  // 下拉選項：把已選客戶併入，確保其不在目前搜尋結果內時仍顯示正確標籤。
  const clientOptions = useMemo(() => {
    const opts = clients.map((c) => ({ value: c.id, label: `[${c.clientCode}] ${c.name}` }));
    if (selectedClient && !opts.some((o) => o.value === selectedClient.id)) {
      opts.unshift({
        value: selectedClient.id,
        label: `[${selectedClient.clientCode}] ${selectedClient.name}`,
      });
    }
    return opts;
  }, [clients, selectedClient]);

  // 申請業務字典（合約來源 / 驗證資格 / 驗證服務類型 / 驗證類別），僅取啟用項
  const sourcesQuery = useContractSources({ activeOnly: true });
  const verificationTypesQuery = useVerificationTypes({ activeOnly: true });
  const categoriesQuery = useVerificationCategories({ activeOnly: true });

  /**
   * 驗證服務類型下拉選項：以後台字典（/settings/verification-types，管理員可維護）為準，
   * 與系統設置後台保持一致。字典為空（種子未跑 / 全被停用）時回退到內建預設清單
   * VERIFICATION_TYPES，避免建案表單無可選項。
   */
  const verificationTypeItems = verificationTypesQuery.data?.items ?? [];
  const verificationTypeOptions =
    verificationTypeItems.length > 0
      ? verificationTypeItems.map((x) => ({ value: x.name, label: x.name }))
      : VERIFICATION_TYPES.map((t) => ({ value: t, label: t }));

  // 條件渲染依賴的監聽值
  const clientId = Form.useWatch('clientId', form);
  const scheme = Form.useWatch('scheme', form);
  const verificationOption = Form.useWatch('verificationOption', form);
  const verificationType = Form.useWatch('verificationType', form);
  const salesChannels = Form.useWatch('salesChannels', form);

  // 客戶土地 / 採後處理場所（場所選定用；選定客戶後才拉取）
  const landsQuery = useQuery<{ items: ClientLandItem[] }>({
    queryKey: ['client-lands-for-new-case', clientId],
    queryFn: () => apiClient.get(`/api/clients/${clientId}/lands`),
    enabled: !!clientId,
  });
  const sitesQuery = useQuery<{ items: ClientSiteItem[] }>({
    queryKey: ['client-sites-for-new-case', clientId],
    queryFn: () => apiClient.get(`/api/clients/${clientId}/processing-sites`),
    enabled: !!clientId,
  });
  const clientLands = landsQuery.data?.items ?? [];
  const clientSites = sitesQuery.data?.items ?? [];

  // 序128（D1）：驗證場所/採收後處理場所地址「新增」入口——後端端點早已存在
  // （POST /api/clients/:id/lands、/processing-sites），本頁補上彈窗表單直接呼叫。
  const queryClient = useQueryClient();
  const [addPlaceOpen, setAddPlaceOpen] = useState<'land' | 'site' | null>(null);
  const [addPlaceForm] = Form.useForm<{ address: string; parcelNo?: string; name?: string }>();
  const createLandMutation = useMutation({
    mutationFn: (body: { address: string; parcelNo?: string }) =>
      apiClient.post<{ item: { id: number } }>(`/api/clients/${clientId}/lands`, body),
    onSuccess: async (res) => {
      await queryClient.invalidateQueries({ queryKey: ['client-lands-for-new-case', clientId] });
      const prev = (form.getFieldValue('selectedLandIds') as number[] | undefined) ?? [];
      form.setFieldValue('selectedLandIds', [...prev, res.item.id]);
      message.success('已新增驗證場所地址並自動勾選');
      setAddPlaceOpen(null);
      addPlaceForm.resetFields();
    },
    onError: (err: Error) => {
      console.error('[dispatch/new] create client land failed', err);
      message.error(err.message || '新增驗證場所地址失敗');
    },
  });
  const createSiteMutation = useMutation({
    mutationFn: (body: { address: string; name?: string }) =>
      apiClient.post<{ item: { id: number } }>(`/api/clients/${clientId}/processing-sites`, body),
    onSuccess: async (res) => {
      await queryClient.invalidateQueries({ queryKey: ['client-sites-for-new-case', clientId] });
      const prev = (form.getFieldValue('selectedSiteIds') as number[] | undefined) ?? [];
      form.setFieldValue('selectedSiteIds', [...prev, res.item.id]);
      message.success('已新增採收後處理場所地址並自動勾選');
      setAddPlaceOpen(null);
      addPlaceForm.resetFields();
    },
    onError: (err: Error) => {
      console.error('[dispatch/new] create client processing site failed', err);
      message.error(err.message || '新增採收後處理場所地址失敗');
    },
  });

  /**
   * 客戶綁定單一驗證方案（clientCode 由其衍生，如 ZH-CAS-0001）。
   * 選定客戶後，案件方案自動帶入客戶方案並鎖定該下拉，避免案件碼前綴與客戶碼不一致。
   */
  useEffect(() => {
    if (selectedClient && form.getFieldValue('scheme') !== selectedClient.scheme) {
      form.setFieldValue('scheme', selectedClient.scheme);
    }
  }, [selectedClient, form]);

  // 切換客戶時清空場所勾選（土地 / 採後場所屬於客戶檔案，跨客戶勾選無意義）
  useEffect(() => {
    form.setFieldValue('selectedLandIds', []);
    form.setFieldValue('selectedSiteIds', []);
  }, [clientId, form]);

  /**
   * 申請明細分流 kind：TAP → 'TAP'；TOP / ECO → 'TOP'（ECO 暫無獨立表樣）。
   * 未選客戶（scheme 未定）時不顯示申請明細區塊。
   */
  const detailKind = scheme ? applicationKindForScheme(scheme) : undefined;

  // 作物字典（序157 已區分 TOP/TAP 方案，此處按申請明細 kind 對應方案篩選；未選客戶前不查）
  const cropsQuery = useCropOptions({
    scheme: detailKind === 'TAP' ? 'TAP' : 'TOP',
    enabled: !!detailKind,
  });
  const cropItems = cropsQuery.data?.items ?? [];

  // TAP 首次顯示時預填一行空明細（省一次點擊；序16 起非必填，空行提交時自動略過）
  useEffect(() => {
    if (
      detailKind === 'TAP' &&
      !(form.getFieldValue('tapRows') as TapRowValue[] | undefined)?.length
    ) {
      form.setFieldValue('tapRows', [{}]);
    }
  }, [detailKind, form]);

  // 序165：TOP 查驗型（追蹤/展延）面積分展示
  const isTrackingArea = isTrackingVerificationType(verificationType);

  const channels = salesChannels ?? [];

  /**
   * 驗證類別依所選方案過濾：
   * 只顯示 scheme 相符或未標方案（通用）的類別；未選方案時顯示全部啟用項。
   */
  const filteredCategories = (categoriesQuery.data?.items ?? []).filter(
    (cat) => !scheme || !cat.scheme || cat.scheme === scheme,
  );

  /** 建立案件 Mutation。成功後跳轉案件詳情頁（合約評審改由詳情頁發起）。 */
  const createCaseMutation = useMutation({
    mutationFn: async (v: FormValues) => {
      // —— 通用欄位（TOP / TAP 共有：驗證選項 / 銷售通路）——（序137 移除輔導員）
      const common: ApplicationDetails = {
        verificationOption: v.verificationOption,
        groupFarmerCount: v.verificationOption === 'group' ? v.groupFarmerCount : undefined,
        salesChannels: v.salesChannels?.length ? v.salesChannels : undefined,
        salesAuctionName: v.salesChannels?.includes('auction')
          ? v.salesAuctionName?.trim() || undefined
          : undefined,
        salesSupermarketName: v.salesChannels?.includes('supermarket')
          ? v.salesSupermarketName?.trim() || undefined
          : undefined,
        salesProcessorName: v.salesChannels?.includes('processor')
          ? v.salesProcessorName?.trim() || undefined
          : undefined,
        salesDistributorName: v.salesChannels?.includes('distributor')
          ? v.salesDistributorName?.trim() || undefined
          : undefined,
        salesOtherName: v.salesChannels?.includes('other')
          ? v.salesOtherName?.trim() || undefined
          : undefined,
      };

      // —— 場所選定（勾選 id + 建案當下地址文字快照；客戶檔案仍是 master）——
      const selectedLandIds = v.selectedLandIds ?? [];
      const selectedSiteIds = v.selectedSiteIds ?? [];
      const siteSelection = {
        selectedLandIds: selectedLandIds.length ? selectedLandIds : undefined,
        selectedSiteIds: selectedSiteIds.length ? selectedSiteIds : undefined,
        landAddressSnapshot: selectedLandIds.length
          ? selectedLandIds
              .map((id) => clientLands.find((l) => l.id === id))
              .filter((l): l is ClientLandItem => !!l)
              .map(landSnapshotText)
          : undefined,
        siteAddressSnapshot: selectedSiteIds.length
          ? selectedSiteIds
              .map((id) => clientSites.find((s) => s.id === id))
              .filter((s): s is ClientSiteItem => !!s)
              .map(siteSnapshotText)
          : undefined,
      };

      // —— 按方案組裝申請明細（TAP → 5 欄多行 + 總面積；TOP / ECO → 品項 / 範圍 / 面積）——
      // 組裝邏輯與修改案件抽屜共用 application-details-form.ts（序14）
      let applicationDetails: CaseApplicationDetails;
      if (applicationKindForScheme(v.scheme) === 'TAP') {
        // 序16：非必填，未選品項的空行略過
        const rows = buildTapRows(v.tapRows);
        applicationDetails = {
          ...common,
          kind: 'TAP',
          rows,
          // 序 56：生產地及場區筆數拆為生產地筆數（landCount）+ 場區筆數（siteCount）
          landCount: v.tapLandCount,
          siteCount: v.tapSiteCount,
          // 前端先算一次供即時顯示；後端寫入前仍會以 rows 重算（normalizeCaseApplicationDetails）
          totalArea: sumTapArea(rows),
          ...siteSelection,
        };
      } else {
        // 序6：驗證品項（字典）＋驗證範圍（自由文字）→ productItems / productScopes
        const { productItems, productScopes } = buildTopProductEntries(v.topProductGroups);
        applicationDetails = {
          ...common,
          kind: 'TOP',
          productItems: productItems.length ? productItems : undefined,
          productScopes: productScopes.length ? productScopes : undefined,
          // 序165：依驗證服務類型分展示的面積 → 統一寫回 verificationArea，並保留分項欄位
          ...buildTopAreaFields(v, v.verificationType),
          ...siteSelection,
        };
      }

      const res = await apiClient.post<{ item: CreatedCase }>('/api/cases', {
        clientId: v.clientId,
        scheme: v.scheme,
        contractSource: v.contractSource,
        // 驗證資格已遷移至客戶檔案（序102）：不再由建案表單傳入，後端自動取客戶檔案值
        // 多選類型以分隔符拼成單一字串，相容既有單欄存儲
        verificationType: joinVerificationTypes(v.verificationType),
        verificationCategories: v.verificationCategories?.length
          ? v.verificationCategories
          : undefined,
        // 新結構永遠帶 kind（TOP / TAP 分流），不再判空省略
        applicationDetails,
        notes: v.notes?.trim() || undefined,
      });
      const created = res.item;
      if (!created) throw new Error('建立案件回應格式異常');
      return created;
    },
    onSuccess: (created) => {
      message.success(`案件 ${created.caseCode} 已建立`);
      // 序109：建案時上傳的【申請表】自動掛載為案件應附文件（分類：申請書）
      if (applicationFile) {
        void apiClient
          .post(`/api/cases/${created.id}/documents`, {
            fileId: applicationFile.id,
            docType: '申請書',
          })
          .catch((err) => {
            console.error('[dispatch/new] attach application form failed', err);
            message.warning('申請表掛載失敗，請至案件詳情「應附文件」補傳');
          });
      }
      void navigate({ to: '/dispatch/$caseId', params: { caseId: String(created.id) } });
    },
    onError: (err: Error) => {
      // 業務必要：建立失敗輸出錯誤日誌，便於排查
      console.error('[dispatch/new] create case failed', err);
      message.error(`建立案件失敗：${err.message ?? '請稍後再試'}`);
    },
  });

  return (
    <PageShell
      title="新建驗證案件"
      description="填寫驗證申請資料並建立案件，建立後可於案件詳情補傳申請文件、發起合約評審"
      actions={
        <Button onClick={() => void navigate({ to: '/dispatch' })} icon={<ArrowLeft size={14} />}>
          返回列表
        </Button>
      }
    >
      {/* 內容滿寬：不再限寬，白色卡片占滿整個內容區寬度（依需求填滿螢幕）。 */}
      <SectionCard icon={<ClipboardList size={14} />} title="驗證申請資料">
        <Form<FormValues>
          form={form}
          layout="vertical"
          requiredMark
          initialValues={{ verificationOption: 'individual', contractSource: '中華驗證' }}
          onFinish={(v) => {
            void (async () => {
              // 序22：排查該客戶本年度是否已建案／派案，有則提示確認，取消即不建立
              const proceed = await confirmYearlyDuplicateCases(
                v.clientId,
                v.verificationType ?? [],
                selectedClient
                  ? `[${selectedClient.clientCode}] ${selectedClient.name}`
                  : undefined,
              );
              if (!proceed) return;
              // 序7：有機案件提交前比對作物管理，確認是否補建尚未建立的驗證範圍（不阻斷建案）
              if (applicationKindForScheme(v.scheme) === 'TOP') {
                await confirmAndEnsureTopScopes(v.topProductGroups, cropItems, () => {
                  void queryClient.invalidateQueries({ queryKey: ['settings', 'crops'] });
                });
              }
              createCaseMutation.mutate(v);
            })();
          }}
        >
          {/* ── 申請表（序109：位置前移到新增驗證案件；上傳後可查看、可讀取填充） ── */}
          <FormSection
            title="申請表"
            description="上傳申請表掃描件：可查看內容，並嘗試讀取數據自動填充下方欄位（提取值可修改）；建案後自動掛載為應附文件"
            bordered
          >
            <Space direction="vertical" size={8} style={{ width: '100%' }}>
              <Space wrap>
                <Upload
                  showUploadList={false}
                  accept=".jpg,.jpeg,.png,.pdf"
                  beforeUpload={(file) => {
                    void (async () => {
                      setApplicationUploading(true);
                      try {
                        const uploaded = await uploadFile(file, { scope: 'case' });
                        setApplicationFile(uploaded);
                        message.success(`申請表已上傳：${uploaded.filename}`);
                      } catch (err) {
                        console.error('[dispatch/new] upload application failed', err);
                        message.error(err instanceof Error ? err.message : '上傳失敗');
                      } finally {
                        setApplicationUploading(false);
                      }
                    })();
                    return false; // 阻止 antd 內建 XHR，改走 OSS 直傳
                  }}
                >
                  <Button loading={applicationUploading}>
                    {applicationFile ? '替換申請表' : '上傳申請表'}
                  </Button>
                </Upload>
                {applicationFile && (
                  <>
                    <FileLink fileId={applicationFile.id} filename={applicationFile.filename} />
                    <Button
                      type="primary"
                      ghost
                      loading={extractMutation.isPending}
                      onClick={() => extractMutation.mutate(applicationFile.id)}
                    >
                      讀取申請表並填充
                    </Button>
                  </>
                )}
              </Space>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                讀取填充目前支援圖片掃描件（JPG/PNG）；提取結果僅供參考，請核對後再提交。
              </Typography.Text>
            </Space>
          </FormSection>

          {/* ── 基本資料 ── */}
          <FormSection title="基本資料" description="選擇客戶、本次驗證方案與合約來源" bordered>
            <Form.Item
              name="clientId"
              label="選擇客戶"
              rules={[{ required: true, message: '請選擇客戶' }]}
            >
              <Select
                showSearch
                placeholder="搜尋客戶名稱或驗證註冊碼…"
                // 伺服器端搜尋（見上方 clientsData 查詢），不再用前端 filterOption 篩選
                // 已拉回的（可能不完整的）本地清單。
                filterOption={false}
                onSearch={setClientSearchInput}
                loading={clientsLoading}
                notFoundContent={clientsLoading ? <Spin size="small" /> : undefined}
                options={clientOptions}
                onChange={(value: number) => {
                  setSelectedClient(clients.find((c) => c.id === value) ?? null);
                }}
                size="large"
              />
            </Form.Item>

            <Space size={16} style={{ display: 'flex' }} align="start">
              <Form.Item
                name="scheme"
                label="驗證方案"
                rules={[{ required: true, message: '請選擇驗證方案' }]}
                tooltip="由所選客戶綁定的驗證方案自動帶入並鎖定，決定案件碼前綴（與客戶驗證註冊碼一致）"
                style={{ flex: 1, marginBottom: 0 }}
              >
                <Select
                  placeholder={selectedClient ? undefined : '請先選擇客戶'}
                  disabled={!!selectedClient}
                  options={VERIFICATION_SCHEMES.map((s) => ({
                    value: s,
                    label: SCHEME_LABELS[s],
                  }))}
                />
              </Form.Item>

              <Form.Item
                name="contractSource"
                label="合約來源"
                rules={[{ required: true, message: '請選擇合約來源' }]}
                style={{ flex: 1, marginBottom: 0 }}
              >
                <Select
                  showSearch
                  allowClear
                  placeholder="請選擇合約來源"
                  loading={sourcesQuery.isLoading}
                  options={(sourcesQuery.data?.items ?? []).map((x) => ({
                    value: x.name,
                    label: x.name,
                  }))}
                  filterOption={(input, opt) =>
                    ((opt?.label as string) ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </Space>
          </FormSection>

          {/* ── 驗證選項與服務 ── */}
          <FormSection
            title="驗證選項與服務"
            description="個別 / 集團驗證、輔導員與驗證服務類型"
            bordered
          >
            <Form.Item name="verificationOption" label="驗證選項">
              <Radio.Group>
                <Radio value="individual">個別驗證</Radio>
                <Radio value="group">集團驗證</Radio>
              </Radio.Group>
            </Form.Item>

            {verificationOption === 'group' && (
              <Form.Item
                name="groupFarmerCount"
                label="農戶數"
                rules={[{ required: true, message: '集團驗證請填寫農戶數' }]}
              >
                <InputNumber
                  min={1}
                  max={99999}
                  precision={0}
                  style={{ width: 200 }}
                  addonAfter="戶"
                />
              </Form.Item>
            )}

            {/* 輔導員 / 輔導員手機欄位已依客戶 2026-07（序137）需求移除 */}

            {/* 驗證資格已遷移至「組織客戶檔案」維護（序102）；建案時後端自動取客戶檔案上的驗證資格 */}
            <Form.Item
              name="verificationType"
              label="驗證服務類型"
              rules={[{ required: true, type: 'array', min: 1, message: '請選擇驗證服務類型' }]}
              tooltip="決定後續稽核任務類型；首次 / 增列為申請型，追蹤 / 展延為查驗型。可複選"
            >
              <Select
                mode="multiple"
                showSearch
                allowClear
                placeholder="選擇驗證服務類型（可複選）"
                loading={verificationTypesQuery.isLoading}
                options={verificationTypeOptions}
                filterOption={(input, opt) =>
                  ((opt?.label as string) ?? '').toLowerCase().includes(input.toLowerCase())
                }
              />
            </Form.Item>

            <Form.Item
              name="verificationCategories"
              label="驗證類別"
              tooltip="產品類別，依驗證方案過濾，可複選：如 有機作物 / 有機加工 / 農糧產品 …"
              style={{ marginBottom: 0 }}
            >
              <Select
                mode="multiple"
                showSearch
                allowClear
                placeholder={scheme ? '請選擇驗證類別（可複選）' : '請先選擇驗證方案'}
                loading={categoriesQuery.isLoading}
                options={filteredCategories.map((x) => ({ value: x.name, label: x.name }))}
                filterOption={(input, opt) =>
                  ((opt?.label as string) ?? '').toLowerCase().includes(input.toLowerCase())
                }
              />
            </Form.Item>
          </FormSection>

          {/* ── 申請明細（按驗證方案分流：TAP 5 欄多行 / TOP 品項·範圍·面積；PPT S9/S10） ── */}
          <FormSection
            title={detailKind === 'TAP' ? '申請明細（產銷履歷）' : '申請明細（有機）'}
            description={
              detailKind === 'TAP'
                ? '選填。每行一個品項：產品類別 / 產品品項 / 作物產期 / 申請面積 / 合理採收量；驗證總面積自動加總（0.0000 公頃）'
                : '先選【驗證品項】（作物管理），再直接輸入核定之【驗證範圍】（多項以「、」分隔）；未建立於作物管理的範圍會於建立案件時提示是否新增；驗證面積依驗證服務類型分展示'
            }
            bordered
          >
            <ApplicationDetailsFields
              form={form}
              detailKind={detailKind}
              cropItems={cropItems}
              cropsLoading={cropsQuery.isLoading}
              isTrackingArea={isTrackingArea}
            />
          </FormSection>

          {/* ── 場所選定（TOP / TAP 共同；自客戶檔案勾選帶入地址快照） ── */}
          <FormSection
            title="驗證場所 / 採收後處理場所"
            description="自客戶檔案的土地與採後處理場所勾選帶入；建案時存地址文字快照，客戶檔案仍是主數據"
            bordered
          >
            {!clientId ? (
              <span style={{ color: 'rgba(0,0,0,0.45)' }}>請先選擇客戶</span>
            ) : (
              <>
                <Form.Item
                  label="驗證場所地址（客戶土地）"
                  name="selectedLandIds"
                  style={{ marginBottom: 8 }}
                >
                  {clientLands.length ? (
                    <Checkbox.Group
                      style={{ display: 'flex', flexDirection: 'column', gap: 4 }}
                      options={clientLands.map((l) => ({
                        value: l.id,
                        label: landSnapshotText(l),
                      }))}
                    />
                  ) : (
                    <Typography.Text type="secondary">該客戶檔案尚無土地資料</Typography.Text>
                  )}
                </Form.Item>
                <Button
                  size="small"
                  icon={<Plus size={14} />}
                  onClick={() => setAddPlaceOpen('land')}
                  style={{ marginBottom: 16 }}
                >
                  新增驗證場所地址
                </Button>
                <Form.Item
                  label="採收後處理場所地址"
                  name="selectedSiteIds"
                  style={{ marginBottom: 8 }}
                >
                  {clientSites.length ? (
                    <Checkbox.Group
                      style={{ display: 'flex', flexDirection: 'column', gap: 4 }}
                      options={clientSites.map((s) => ({
                        value: s.id,
                        label: siteSnapshotText(s),
                      }))}
                    />
                  ) : (
                    <Typography.Text type="secondary">
                      該客戶檔案尚無採後處理場所資料
                    </Typography.Text>
                  )}
                </Form.Item>
                <Button
                  size="small"
                  icon={<Plus size={14} />}
                  onClick={() => setAddPlaceOpen('site')}
                  style={{ marginBottom: 0 }}
                >
                  新增採收後處理場所地址
                </Button>
              </>
            )}
          </FormSection>

          {/* 序128（D1）：新增驗證場所/採收後處理場所地址彈窗——即時 POST 到客戶檔案並自動勾選 */}
          <Modal
            title={addPlaceOpen === 'land' ? '新增驗證場所地址' : '新增採收後處理場所地址'}
            open={!!addPlaceOpen}
            onCancel={() => {
              setAddPlaceOpen(null);
              addPlaceForm.resetFields();
            }}
            onOk={() => addPlaceForm.submit()}
            confirmLoading={createLandMutation.isPending || createSiteMutation.isPending}
            destroyOnHidden
          >
            <Form
              form={addPlaceForm}
              layout="vertical"
              onFinish={(v: { address: string; parcelNo?: string; name?: string }) => {
                if (addPlaceOpen === 'land') {
                  createLandMutation.mutate({ address: v.address, parcelNo: v.parcelNo });
                } else if (addPlaceOpen === 'site') {
                  createSiteMutation.mutate({ address: v.address, name: v.name });
                }
              }}
            >
              <Form.Item
                name="address"
                label="地址"
                rules={[{ required: true, message: '請填寫地址' }]}
              >
                <Input placeholder="請輸入地址" maxLength={500} />
              </Form.Item>
              {addPlaceOpen === 'land' ? (
                <Form.Item name="parcelNo" label="地號（選填）">
                  <Input placeholder="請輸入地號" maxLength={100} />
                </Form.Item>
              ) : (
                <Form.Item name="name" label="場所名稱（選填）">
                  <Input placeholder="請輸入場所名稱" maxLength={200} />
                </Form.Item>
              )}
            </Form>
          </Modal>

          {/* ── 產品銷售對象 / 通路 ── */}
          <FormSection
            title="產品銷售對象 / 通路"
            description="包括預計要銷售的對象 / 通路，可複選"
            bordered
          >
            <Form.Item name="salesChannels" label="銷售對象 / 通路">
              <Checkbox.Group options={SALES_CHANNEL_OPTIONS} />
            </Form.Item>

            {/* 序131：拍賣市場 / 超市賣場勾選後可輸入名稱；
                2026-09-12 回報序13：各名稱欄補操作提示「支援輸入多項」（以頓號分隔） */}
            {channels.includes('auction') && (
              <Form.Item name="salesAuctionName" label="拍賣市場名稱" extra={SALES_NAME_MULTI_HINT}>
                <Input placeholder="例：台北第一果菜批發市場、三重果菜市場" maxLength={100} />
              </Form.Item>
            )}
            {channels.includes('supermarket') && (
              <Form.Item
                name="salesSupermarketName"
                label="超市賣場名稱"
                extra={SALES_NAME_MULTI_HINT}
              >
                <Input placeholder="例：全聯、家樂福" maxLength={100} />
              </Form.Item>
            )}
            {channels.includes('processor') && (
              <Form.Item
                name="salesProcessorName"
                label="加工業者名稱"
                extra={SALES_NAME_MULTI_HINT}
              >
                <Input placeholder="業者名稱（支援輸入多項）" maxLength={100} />
              </Form.Item>
            )}
            {channels.includes('distributor') && (
              <Form.Item
                name="salesDistributorName"
                label="分裝 / 流通業者名稱"
                extra={SALES_NAME_MULTI_HINT}
              >
                <Input placeholder="業者名稱（支援輸入多項）" maxLength={100} />
              </Form.Item>
            )}
            {channels.includes('other') && (
              <Form.Item
                name="salesOtherName"
                label="其他銷售對象名稱"
                extra={SALES_NAME_MULTI_HINT}
                style={{ marginBottom: 0 }}
              >
                <Input placeholder="業者名稱（支援輸入多項）" maxLength={100} />
              </Form.Item>
            )}
          </FormSection>

          {/* ── 備注 ── */}
          <FormSection title="其他" description="案件補充說明（選填）">
            <Form.Item name="notes" label="備注" style={{ marginBottom: 0 }}>
              <Input.TextArea rows={3} maxLength={2000} showCount placeholder="案件備注（選填）" />
            </Form.Item>
          </FormSection>

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Space>
              <Button onClick={() => void navigate({ to: '/dispatch' })}>取消</Button>
              <Button type="primary" htmlType="submit" loading={createCaseMutation.isPending}>
                建立案件
              </Button>
            </Space>
          </div>
        </Form>
      </SectionCard>
    </PageShell>
  );
}
